import { ExpandLess, ExpandMore } from '@mui/icons-material'
import CasinoIcon from '@mui/icons-material/Casino'
import {
  Collapse,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
} from '@mui/material'
import { permissionValidation } from '@novafuturltd/shared'
import { useRouter } from 'next/router'
import { useContext, useState } from 'react'
import { useIntl } from 'react-intl'
import { TtoggleDrawerOnMobile } from '../../../containers/Sidebar/Sidebar'
import { useStyles } from '../../../containers/Sidebar/Sidebar.styles'
import { MixpanelContext } from '../../../contexts/mixpanel'
import { FeatureToggle } from '../../../contexts/toggle-feature'
import { MenuMiniGames } from '../mini-games-ordering/sidebar/MenuMiniGames'
import { MenuSwipeGames } from '../swipe-games/sidebar/MenuSwipeGames'
import { MenuBetList } from './sub-menu/MenuBetList'
import { MenuCategories } from './sub-menu/MenuCategories'
import { MenuCategoriesOrdering } from './sub-menu/MenuCategoriesOrdering'
import { MenuGameList } from './sub-menu/MenuGameList'
import { MenuLayout } from './sub-menu/MenuLayout'
import { MenuSections } from './sub-menu/MenuSections'
import MenuCacheManagement from '../cache-purge/sidebar/MenuCacheManagement'

interface CasinoSubMenuProps {
  hoverDrawer?: boolean
  openDrawer?: boolean
  permissions: string[]
  toggleDrawerOnMobile: TtoggleDrawerOnMobile
}

export const CasinoSubMenu = ({
  permissions,
  toggleDrawerOnMobile,
  openDrawer,
  hoverDrawer,
}: CasinoSubMenuProps) => {
  const { formatMessage } = useIntl()
  const { pathname } = useRouter()
  const classes = useStyles({ openDrawer, hoverDrawer })
  const [open, setOpen] = useState(pathname.includes('/casino'))
  const mxp = useContext(MixpanelContext)

  const hasCasinoAdminPermission = permissionValidation(
    permissions,
    'casino.bets.*',
  )

  const hasCasinoBetListPermission =
    hasCasinoAdminPermission ||
    permissionValidation(permissions, 'casino.bets.list')

  const handleClick = () => {
    setOpen(!open)
    mxp.track('click', {
      element: 'casino menu click',
      section: 'sidebar',
    })
  }

  return hasCasinoBetListPermission ? (
    <MenuList aria-labelledby="nested-list-subheader" className={classes.root}>
      <MenuItem className={classes.menuItem} onClick={handleClick}>
        <ListItemIcon>
          <CasinoIcon />
        </ListItemIcon>
        <ListItemText
          hidden={!openDrawer && !hoverDrawer}
          primary={formatMessage({
            id: 'casino.menu.title',
          })}
        />
        {open ? (
          <ExpandLess className={classes.expandIcon} />
        ) : (
          <ExpandMore className={classes.expandIcon} />
        )}
      </MenuItem>

      <Collapse
        in={open}
        timeout="auto"
        unmountOnExit
        hidden={!openDrawer && !hoverDrawer}
      >
        <MenuList component="div" disablePadding>
          <MenuBetList
            pathname={pathname}
            toggleDrawerOnMobile={toggleDrawerOnMobile}
            nested={
              openDrawer || hoverDrawer ? classes.nested : classes.nestedClosed
            }
          />

          {hasCasinoAdminPermission && (
            <>
              <FeatureToggle.On name="casino:categories">
                <MenuCategories
                  pathname={pathname}
                  toggleDrawerOnMobile={toggleDrawerOnMobile}
                  nested={
                    openDrawer || hoverDrawer
                      ? classes.nested
                      : classes.nestedClosed
                  }
                />
              </FeatureToggle.On>

              <FeatureToggle.On name="casino:categories-ordering">
                <MenuCategoriesOrdering
                  pathname={pathname}
                  toggleDrawerOnMobile={toggleDrawerOnMobile}
                  nested={
                    openDrawer || hoverDrawer
                      ? classes.nested
                      : classes.nestedClosed
                  }
                />
              </FeatureToggle.On>

              <FeatureToggle.On name="casino:sections">
                <MenuSections
                  pathname={pathname}
                  toggleDrawerOnMobile={toggleDrawerOnMobile}
                  nested={
                    openDrawer || hoverDrawer
                      ? classes.nested
                      : classes.nestedClosed
                  }
                />
              </FeatureToggle.On>

              <FeatureToggle.On name="casino:game-list">
                <MenuGameList
                  pathname={pathname}
                  toggleDrawerOnMobile={toggleDrawerOnMobile}
                  nested={
                    openDrawer || hoverDrawer
                      ? classes.nested
                      : classes.nestedClosed
                  }
                />
              </FeatureToggle.On>

              <FeatureToggle.On name="casino:layout">
                <MenuLayout
                  pathname={pathname}
                  toggleDrawerOnMobile={toggleDrawerOnMobile}
                  nested={
                    openDrawer || hoverDrawer
                      ? classes.nested
                      : classes.nestedClosed
                  }
                />
              </FeatureToggle.On>

              <FeatureToggle.On name="casino:swipe-games-ordering">
                <MenuSwipeGames
                  pathname={pathname}
                  toggleDrawerOnMobile={toggleDrawerOnMobile}
                  nested={
                    openDrawer || hoverDrawer
                      ? classes.nested
                      : classes.nestedClosed
                  }
                />
              </FeatureToggle.On>

              <FeatureToggle.On name="casino:mini-games-ordering">
                <MenuMiniGames
                  pathname={pathname}
                  toggleDrawerOnMobile={toggleDrawerOnMobile}
                  nested={
                    openDrawer || hoverDrawer
                      ? classes.nested
                      : classes.nestedClosed
                  }
                />
              </FeatureToggle.On>
            </>
          )}

          <MenuCacheManagement
            pathname={pathname}
            toggleDrawerOnMobile={toggleDrawerOnMobile}
            nested={
              openDrawer || hoverDrawer ? classes.nested : classes.nestedClosed
            }
          />
        </MenuList>
      </Collapse>
    </MenuList>
  ) : null
}
